import { z } from "zod";

/**
 * Shared Zod schemas for `Company_Input` and `Roast_Result`.
 *
 * This module is imported by both the browser SPA and the Netlify Function
 * proxy, so it must stay free of any client- or DOM-specific imports.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Data Models".
 */

/**
 * Visitor-supplied company input captured by the entry screen.
 *
 * - `companyName` is trimmed and must be 1..100 characters.
 * - `marketingDescription` is trimmed and must be 1..1000 characters.
 */
export const CompanyInput = z.object({
  companyName: z.string().trim().min(1).max(100),
  marketingDescription: z.string().trim().min(1).max(1000),
});

/**
 * A single actionable marketing recommendation.
 *
 * Trimmed, 1..280 characters.
 */
export const Recommendation = z.string().trim().min(1).max(280);

/**
 * The complete structured output returned by the Roast_Generator.
 *
 * Invariants enforced here match the acceptance criteria in Requirement 2:
 * - `roastText`: 1..1200 chars
 * - `cringeScore`: integer in [1, 10]
 * - `cringeLabel`: 1..60 chars
 * - `recommendations`: exactly 3 entries (each a `Recommendation`)
 * - `closingLine`: 1..240 chars
 * - `cta`: 1..200 chars AND must invite the visitor to leave contact details
 */
export const RoastResult = z.object({
  roastText: z.string().trim().min(1).max(1200),
  cringeScore: z.number().int().min(1).max(10),
  cringeLabel: z.string().trim().min(1).max(60),
  recommendations: z.array(Recommendation).length(3),
  closingLine: z.string().trim().min(1).max(240),
  cta: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .refine((s) => /contact/i.test(s), {
      message: "cta must invite the visitor to leave contact details",
    }),
});

/** Inferred TypeScript type for `CompanyInput`. */
export type CompanyInputT = z.infer<typeof CompanyInput>;

/** Inferred TypeScript type for `RoastResult`. */
export type RoastResultT = z.infer<typeof RoastResult>;

/** Inferred TypeScript type for a single `Recommendation`. */
export type RecommendationT = z.infer<typeof Recommendation>;
